import Link from "next/link";
import type { DefaultValues } from "react-hook-form";
import type { CamperContactFormDataT } from "@/app/schemas/zod/contact-form-schema";
import type {
  ContactFormConfigT,
  ContactFormFieldConfigT,
} from "./contact-form-types";

export type CamperContactFormFieldNameT =
  | "location"
  | "make"
  | "company"
  | "salutation"
  | "lastname"
  | "firstname"
  | "email"
  | "phone"
  | "vehicleLicensePlate"
  | "vehicleVin"
  | "vehicleMileage"
  | "vehicleModel"
  | "comments"
  | "privacy"
  | "mail"
  | "case"
  | "URL"
  | "source"
  | "dealerdesk";

export const camperContactFormDefaultValues: DefaultValues<CamperContactFormDataT> =
  {
    location: "",
    make: "",
    company: undefined,
    salutation: undefined,
    lastname: "",
    firstname: "",
    email: "",
    phone: "",
    vehicleLicensePlate: undefined,
    vehicleVin: undefined,
    vehicleMileage: undefined,
    vehicleModel: undefined,
    comments: undefined,
    privacy: false,
    mail: "",
    case: "CASE",
    URL: undefined,
    source: "Camper Anfrage",
    dealerdesk: "Service NFZ Darmstadt",
  };

const primaryFields: ContactFormFieldConfigT<CamperContactFormFieldNameT>[] = [
  {
    name: "location",
    type: "select",
    label: "Standort",
    placeholder: "Standort wählen...",
  },
  {
    name: "make",
    type: "select",
    label: "Marke",
    placeholder: "Um welche Marke geht es?",
  },
  {
    name: "company",
    type: "text",
    label: "Firma",
    placeholder: "Firma",
  },
  {
    name: "salutation",
    type: "select",
    label: "Anrede",
    placeholder: "Bitte auswählen...",

    options: [
      { value: "Herr", label: "Herr" },
      { value: "Frau", label: "Frau" },
      { value: "Divers", label: "Divers" },
    ],
  },
  {
    name: "firstname",
    type: "name",
    label: "Vorname",
    placeholder: "Vorname",
  },
  {
    name: "lastname",
    type: "name",
    label: "Name",
    placeholder: "Nachname",
  },
  {
    name: "email",
    type: "text",
    inputType: "email",
    label: "E-Mail",
    placeholder: "E-Mail",
  },
  {
    name: "phone",
    type: "phone",
    label: "Telefon",
    placeholder: "+49 (123) 45678902",
  },
];

const vehicleFields: ContactFormFieldConfigT<CamperContactFormFieldNameT>[] = [
  {
    name: "vehicleLicensePlate",
    type: "text",
    label: "Kennzeichen",
    placeholder: "Kennzeichen",
  },
  {
    name: "vehicleVin",
    type: "text",
    label: "FIN",
    placeholder: "FIN",
  },
  {
    name: "vehicleMileage",
    type: "mileage",
    label: "Kilometerstand",
    placeholder: "Kilometerstand",
    unit: "km",
  },
  {
    name: "vehicleModel",
    type: "text",
    label: "Modell",
    placeholder: "Modellbeschreibung",
  },
];

const commentsField: ContactFormFieldConfigT<CamperContactFormFieldNameT> = {
  name: "comments",
  type: "textarea",
  label: "Gibt es weitere Bemerkungen oder Hinweise?",
  placeholder: "Ihre Nachricht...",
  colSpan: 2,
};

const privacyField: ContactFormFieldConfigT<CamperContactFormFieldNameT> = {
  name: "privacy",
  type: "checkbox",
  colSpan: 2,
  label: (
    <span className="text-sm leading-relaxed whitespace-normal sm:whitespace-nowrap">
      Mit absenden bestätigen Sie unsere{" "}
      <Link href="/datenschutz" className="underline">
        Datenschutzerklärung
      </Link>
      .
    </span>
  ),
};

const honeypotField: ContactFormFieldConfigT<CamperContactFormFieldNameT> = {
  name: "mail",
  type: "honeypot",
};

const caseField: ContactFormFieldConfigT<CamperContactFormFieldNameT> = {
  name: "case",
  type: "select",
  hidden: true,
  options: [
    { value: "CASE", label: "CASE" },
    { value: "OPPORTUNITY", label: "OPPORTUNITY" },
  ],
};

const urlField: ContactFormFieldConfigT<CamperContactFormFieldNameT> = {
  name: "URL",
  type: "text",
  hidden: true,
};

export const camperContactFormConfig: ContactFormConfigT<CamperContactFormFieldNameT> =
  {
    sections: [
      {
        id: "primary",
        layout: "grid",
        columns: 2,
        fields: primaryFields,
      },
      {
        id: "vehicle",
        title: "Fahrzeugdaten (optional)",
        collapsible: true,
        defaultExpanded: false,
        layout: "grid",
        columns: 2,
        fields: vehicleFields,
      },
      {
        id: "additional",
        layout: "grid",
        columns: 2,
        fields: [
          commentsField,
          privacyField,
          honeypotField,
          caseField,
          urlField,
        ],
      },
    ],
    honeypotFieldName: "mail",
    submitButtonLabel: "Nachricht senden",
    submittingLabel: "Wird gesendet...",
    successToast: {
      title: "Nachricht erfolgreich gesendet",
      description: "Wir werden uns in Kürze bei Ihnen melden.",
    },
    errorToast: {
      title: "Fehler beim Senden",
      description:
        "Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.",
    },
  };
